import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import * as React from 'react';
import { createMuiTheme, CssBaseline, ThemeProvider, makeStyles } from '@material-ui/core';
import deepmerge from 'deepmerge';
import { designTokens } from './Theme.design-tokens';
var theme = createMuiTheme(); // CONSTANTS

export var brand500Colors = [designTokens.colors.navy500, designTokens.colors.teal500, designTokens.colors.red500, designTokens.colors.aqua500, designTokens.colors.blueGray500, designTokens.colors.gray500];
var basePalette = {
  common: {
    black: designTokens.colors.black,
    white: designTokens.colors.white
  },
  grey: {
    50: designTokens.colors.gray050,
    100: designTokens.colors.gray100,
    200: designTokens.colors.gray200,
    300: designTokens.colors.gray300,
    400: designTokens.colors.gray400,
    500: designTokens.colors.gray500,
    600: designTokens.colors.gray600,
    700: designTokens.colors.gray700,
    800: designTokens.colors.gray800,
    900: designTokens.colors.gray900
  }
};
export var lightPalette = deepmerge(basePalette, {
  primary: {
    main: designTokens.colors.blue500
  },
  secondary: {
    main: designTokens.colors.teal600
  },
  error: {
    main: designTokens.colors.red500
  },
  warning: {
    main: designTokens.colors.orange600
  },
  info: {
    main: designTokens.colors.blue600
  },
  success: {
    main: designTokens.colors.green500
  },
  background: {
    default: designTokens.colors.lightPageBackground
  }
});
export var darkPalette = deepmerge(basePalette, {
  primary: {
    main: designTokens.colors.blue200
  },
  secondary: {
    main: designTokens.colors.teal100
  },
  error: {
    main: designTokens.colors.red200
  },
  warning: {
    main: designTokens.colors.orange200
  },
  info: {
    main: designTokens.colors.blue200
  },
  success: {
    main: designTokens.colors.green200
  },
  type: 'dark',
  background: {
    default: designTokens.colors.darkMainContainer,
    paper: designTokens.colors.navy700
  }
});
export var emphasis = {
  light: {
    low: {
      color: designTokens.colors.lightEmphasisLow
    },
    medium: {
      color: designTokens.colors.lightEmphasisMedium
    },
    high: {
      color: designTokens.colors.lightEmphasisHigh
    }
  },
  dark: {
    low: {
      color: designTokens.colors.darkEmphasisLow
    },
    medium: {
      color: designTokens.colors.darkEmphasisMedium
    },
    high: {
      color: designTokens.colors.darkEmphasisHigh
    }
  }
};

var typography = _objectSpread(_objectSpread({
  fontFamily: ['Muli', 'Helvetica', 'Arial', 'sans-serif'].join(',')
}, Object.keys(theme.typography).map(function (key) {
  var typedKey = key;
  return _objectSpread(_objectSpread({}, theme.typography[typedKey]), designTokens.fonts[typedKey]);
})), {}, {
  overline: _objectSpread(_objectSpread({}, theme.typography.overline), designTokens.fonts.overline1)
}); // CREATOR FUNCTIONS


var createInputErrorState = function createInputErrorState(palette) {
  var _palette$background;

  return {
    '& fieldset': {
      width: '100%'
    },
    '& input': {
      flexGrow: 1
    },
    '& *:not(fieldset)': {
      zIndex: 1
    },
    '&::before': {
      content: "'error'",
      borderRadius: 'inherit',
      // @ts-ignore
      backgroundColor: "".concat(palette.error.main, " !important"),
      color: palette === null || palette === void 0 ? void 0 : (_palette$background = palette.background) === null || _palette$background === void 0 ? void 0 : _palette$background.default,
      fontFamily: 'Material Icons Outlined',
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      fontSize: '24px',
      opacity: 0
    },
    '&::after': {
      display: 'flex',
      opacity: 0,
      content: "''",
      flexBasis: '0px'
    },
    '&.Mui-error': {
      '& fieldset': {
        width: 'calc(100% - 40px)',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      },
      '& .MuiSelect-icon, .MuiAutocomplete-endAdornment': {
        transform: 'translateX(-40px)'
      },
      '&::after': {
        flexBasis: '51px'
      },
      '&::before': {
        opacity: 1
      }
    }
  };
};

export var emphasisStyles = makeStyles(function (theme) {
  return emphasis[theme.palette.type];
}); // THEMES

export var baseTheme = {
  overrides: {
    MuiAccordion: {
      rounded: {
        '&:first-child': {
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2
        },
        '&:last-child': {
          borderBottomLeftRadius: 2,
          borderBottomRightRadius: 2
        }
      }
    },
    MuiAccordionActions: {
      root: {
        '&:empty': {
          padding: 0
        }
      }
    },
    MuiAccordionDetails: {
      root: {
        padding: theme.spacing(2)
      }
    },
    MuiAccordionSummary: {
      root: {
        '&$expanded': {
          minHeight: 0
        }
      },
      content: {
        '&$expanded': {
          margin: "".concat(theme.spacing(2), "px 0")
        }
      }
    },
    MuiAlert: {
      action: {
        alignItems: 'start',
        marginTop: '2px'
      }
    },
    MuiButton: {
      root: {
        fontWeight: 500
      }
    },
    MuiCard: {
      root: {
        borderRadius: 2
      }
    },
    MuiCheckbox: {
      root: {
        padding: theme.spacing(1)
      }
    },
    MuiInputAdornment: {
      positionStart: {
        marginRight: 0
      },
      positionEnd: {
        marginLeft: 0
      }
    },
    MuiInputLabel: {
      root: {
        transform: 'none !important',
        display: 'flex',
        alignItems: 'center'
      },
      formControl: {
        position: 'relative'
      }
    },
    MuiOutlinedInput: {
      adornedStart: {
        paddingLeft: 0
      },
      adornedEnd: {
        paddingRight: 0
      },
      notchedOutline: {
        top: 0,
        '& legend': {
          height: 0
        }
      }
    },
    MuiFormLabel: {
      // @ts-ignore
      root: _objectSpread(_objectSpread({}, typography.overline), {}, {
        lineHeight: 'inherit',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center'
      }),
      asterisk: {
        '&.Mui-error': {
          color: 'transparent'
        },
        height: '8px',
        width: '8px',
        borderRadius: '50%',
        backgroundColor: designTokens.colors.teal600,
        marginLeft: '8px',
        color: 'transparent',
        marginTop: '-0.5px'
      }
    },
    MuiLink: {
      root: {
        cursor: 'pointer'
      }
    },
    MuiTab: {
      root: {
        fontWeight: 700,
        letterSpacing: '1.1px'
      }
    },
    MuiTable: {
      root: {
        borderCollapse: 'inherit'
      }
    },
    MuiTableCell: {
      root: {
        '&.MuiTableCell-sizeSmall.MuiTableCell-paddingCheckbox': {
          padding: '0 12px 0 12px'
        }
      }
    },
    MuiFormHelperText: {
      contained: {
        marginLeft: 0
      }
    },
    MuiTypography: {
      gutterBottom: {
        marginBottom: theme.spacing(1)
      }
    },
    MuiSwitch: {
      switchBase: {
        padding: theme.spacing(1)
      }
    }
  },
  props: {
    MuiInputLabel: {
      shrink: true
    },
    MuiOutlinedInput: {
      notched: false
    }
  },
  // @ts-ignore
  typography: typography,
  palette: basePalette
};
export var lightTheme = createMuiTheme(deepmerge(baseTheme, {
  overrides: {
    MuiAccordionDetails: {
      root: {
        borderTop: "1px solid ".concat(designTokens.colors.lightContextHigh)
      }
    },
    MuiFormLabel: {
      root: _objectSpread({}, emphasis.light.high)
    },
    MuiIcon: {
      colorAction: {
        color: emphasis.light.medium.color
      }
    },
    MuiInputBase: {
      root: _objectSpread({}, createInputErrorState(lightPalette))
    }
  },
  palette: lightPalette
}));
export var darkTheme = createMuiTheme(deepmerge(baseTheme, {
  overrides: {
    MuiAccordionDetails: {
      root: {
        borderTop: "1px solid ".concat(designTokens.colors.darkContextHigh)
      }
    },
    MuiSwitch: {
      colorPrimary: {
        '&.Mui-checked': {
          color: designTokens.colors.blue500
        },
        '&.Mui-disabled': {
          color: 'inherit',
          opacity: 0.5
        }
      }
    },
    MuiFormLabel: {
      root: _objectSpread({}, emphasis.dark.high)
    },
    MuiIcon: {
      colorAction: {
        color: emphasis.dark.medium.color
      }
    },
    MuiInputBase: {
      root: _objectSpread({}, createInputErrorState(darkPalette))
    }
  },
  palette: darkPalette
}));
export var LightThemeProvider = function LightThemeProvider(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: lightTheme
  }, /*#__PURE__*/React.createElement(CssBaseline, null), children);
};
export var DarkThemeProvider = function DarkThemeProvider(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: darkTheme
  }, /*#__PURE__*/React.createElement(CssBaseline, null), children);
};

try {
  emphasisStyles.__docgenInfo = {
    description: '',
    displayName: 'emphasisStyles',
    methods: [],
    props: {}
  };
} catch (e) {}

try {
  LightThemeProvider.__docgenInfo = {
    description: '',
    displayName: 'LightThemeProvider',
    methods: [],
    props: {}
  };
} catch (e) {}

try {
  DarkThemeProvider.__docgenInfo = {
    description: '',
    displayName: 'DarkThemeProvider',
    methods: [],
    props: {}
  };
} catch (e) {}
//# sourceMappingURL=Theme.js.map